// Tab 2: Liste aprovizionare — auto-generated lists per supplier.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Download, Printer, Trash2, Loader2, Send, CheckCircle2, ListChecks,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

import {
  listPurchaseLists, generatePurchaseLists, listSuppliers,
  setPurchaseListStatus, deletePurchaseList, purchaseListCsvUrl,
  getPurchaseList,
} from "@/lib/inventory";
import { fmtDate } from "@/lib/format";

const STATUS = {
  draft: { label: "Draft", className: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30" },
  sent: { label: "Trimisă", className: "bg-blue-500/10 text-blue-500 border-blue-500/30" },
  received: { label: "Recepționată", className: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" },
};

export default function PurchaseListsTab({ onChanged }) {
  const [lists, setLists] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [view, setView] = useState(null); // selected list for print preview

  const reload = useCallback(async () => {
    setLoading(true);
    try { setLists(await listPurchaseLists({})); }
    catch { toast.error("Eroare"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { listSuppliers().then(setSuppliers).catch(() => {}); }, []);

  const generate = async (supplier_id = null) => {
    setGenerating(true);
    const tid = toast.loading("Generez listele...");
    try {
      const r = await generatePurchaseLists(supplier_id);
      if (r.lists?.length) {
        toast.success(`${r.lists.length} listă/e generate`, { id: tid });
      } else {
        toast.info(r.message || "Nimic de aprovizionat", { id: tid });
      }
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error("Eroare la generare", { id: tid });
    } finally { setGenerating(false); }
  };

  const changeStatus = async (id, status) => {
    try {
      await setPurchaseListStatus(id, status);
      toast.success(`Status: ${status}`);
      await reload();
      onChanged?.();
    } catch { toast.error("Eroare"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Ștergi această listă?")) return;
    await deletePurchaseList(id);
    await reload();
    onChanged?.();
  };

  const openView = async (id) => {
    const pl = await getPurchaseList(id);
    setView(pl);
  };

  return (
    <div className="space-y-4" data-testid="purchase-lists-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Select onValueChange={(v) => generate(v === "ALL" ? null : v)}>
          <SelectTrigger className="h-9 w-[260px]" data-testid="generate-pl-select">
            <SelectValue placeholder={generating ? "Se generează..." : "Generează listă"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Pentru toți furnizorii</SelectItem>
            {suppliers.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-9" onClick={() => generate(null)} disabled={generating} data-testid="generate-all-btn">
          {generating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
          Generează tot
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : lists.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <ListChecks className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <div className="text-sm">Nicio listă de aprovizionare încă.</div>
          <div className="text-xs mt-1">Apasă „Generează tot" pentru a crea liste pe baza pragurilor minime.</div>
        </Card>
      ) : (
        <div className="space-y-2">
          {lists.map((pl) => {
            const st = STATUS[pl.status] || STATUS.draft;
            return (
              <Card key={pl.id} className="p-4" data-testid={`pl-${pl.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-display font-semibold text-base">{pl.supplier_name}</div>
                      <Badge variant="outline" className={`text-[10px] ${st.className}`}>{st.label}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {pl.items.length} SKU · {pl.total_items} buc · {pl.total_value} RON · creată {fmtDate(pl.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" className="h-8" onClick={() => openView(pl.id)} data-testid={`pl-view-${pl.id}`}>
                      <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" asChild data-testid={`pl-csv-${pl.id}`}>
                      <a href={purchaseListCsvUrl(pl.id)} download>
                        <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
                      </a>
                    </Button>
                    {pl.status === "draft" && (
                      <Button size="sm" variant="outline" className="h-8" onClick={() => changeStatus(pl.id, "sent")} data-testid={`pl-send-${pl.id}`}>
                        <Send className="h-3.5 w-3.5 mr-1.5" /> Marchează trimisă
                      </Button>
                    )}
                    {pl.status === "sent" && (
                      <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => changeStatus(pl.id, "received")} data-testid={`pl-receive-${pl.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Recepționează
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-8 text-muted-foreground hover:text-red-500" onClick={() => remove(pl.id)} data-testid={`pl-del-${pl.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Print preview */}
      <PrintDialog list={view} onClose={() => setView(null)} />
    </div>
  );
}


function PrintDialog({ list, onClose }) {
  const printIt = () => window.print();

  return (
    <Dialog open={!!list} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="pl-print-dialog">
        <DialogHeader>
          <DialogTitle>Listă aprovizionare — {list?.supplier_name}</DialogTitle>
          <DialogDescription>Previzualizare pentru printare sau export.</DialogDescription>
        </DialogHeader>
        {list && (
          <div className="space-y-3" id="print-region">
            <div className="grid grid-cols-3 gap-3 text-sm pb-3 border-b">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Furnizor</div>
                <div className="font-medium">{list.supplier_name}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Total SKU</div>
                <div className="font-medium">{list.items.length}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Total estimat</div>
                <div className="font-medium font-mono">{list.total_value} RON</div>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-semibold">SKU</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-semibold">Produs</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-semibold">Stoc fizic</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-semibold">Rezervat</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-semibold">Prag</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-semibold">Recomandat</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-semibold">Cost unitar</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-semibold">Valoare totală</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((it, i) => (
                  <tr key={i} className="border-b border-border/30" data-testid={`pl-row-${it.sku || i}`}>
                    <td className="px-3 py-2 font-mono text-xs">{it.sku || "—"}</td>
                    <td className="px-3 py-2">{it.title}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{it.warehouse_qty ?? "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-blue-500">{it.reserved || 0}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-muted-foreground">{it.min_stock || 0}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-bold text-amber-500">{it.qty_needed}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-muted-foreground">{it.supplier_cost || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{it.line_total || "—"}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-foreground/20 bg-secondary/30 font-semibold">
                  <td className="px-3 py-2" colSpan={5}>TOTAL</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{list.total_items}</td>
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{list.total_value} RON</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-3 print:hidden">
          <Button variant="outline" onClick={onClose}>Închide</Button>
          <Button onClick={printIt} data-testid="pl-print-btn"><Printer className="h-4 w-4 mr-2" /> Tipărește</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
